import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  TablePagination,
  Skeleton,
  Button,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Search,
  Refresh,
  Description,
  AttachMoney,
  CalendarToday,
  Business,
  Visibility,
  CheckCircle,
  Warning,
  Schedule,
  Cancel,
  Autorenew,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import crmService, { Contract, Account } from '../services/crmService';

const ContractsPage: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Detail dialog state
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [viewingContract, setViewingContract] = useState<Contract | null>(null);

  // Fetch contracts
  const fetchContracts = async () => {
    try {
      setLoading(true);
      const response = await crmService.getContracts();
      const contractsData = Array.isArray(response) ? response :
                          response.contracts ? response.contracts :
                          response.data ? response.data : [];
      setContracts(contractsData);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setSnackbar({ open: true, message: 'Failed to fetch contracts', severity: 'error' });
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  // Get account name from contract
  const getAccountName = (account: string | Account | undefined): string => {
    if (!account) return '-';
    if (typeof account === 'string') return account;
    return account.name || '-';
  };

  // Filter contracts based on search
  const filteredContracts = contracts.filter(contract =>
    `${contract.contractNumber} ${contract.name} ${getAccountName(contract.account)}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status color
  const getStatusColor = (status: string) => {
    const colors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      'Active': 'success',
      'Draft': 'default',
      'In Approval': 'info',
      'Expired': 'error',
      'Terminated': 'error',
      'Renewed': 'info',
    };
    return colors[status] || 'default';
  };

  // Get owner name from contract
  const getOwnerName = (owner: string | { _id: string; name: string; email: string } | undefined): string => {
    if (!owner) return '-';
    if (typeof owner === 'string') return owner;
    return owner.name || '-';
  };

  // Format currency
  const formatCurrency = (amount: number, currency?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  // Format date
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate days until expiration
  const getDaysUntilExpiration = (endDate: Date | string | undefined) => {
    if (!endDate) return null;
    const now = new Date();
    const end = new Date(endDate);
    const diff = end.getTime() - now.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  // Handle view contract details
  const handleViewContract = (contract: Contract) => {
    setViewingContract(contract);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setViewingContract(null);
  };

  // Stats calculations
  const totalContracts = contracts.length;
  const activeContracts = contracts.filter(c => c.status === 'Active').length;
  const expiringSoon = contracts.filter(c => {
    const days = getDaysUntilExpiration(c.endDate);
    return days !== null && days > 0 && days <= 30 && c.status === 'Active';
  }).length;
  const totalValue = contracts.reduce((sum, c) => sum + (c.contractValue || 0), 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Description sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{totalContracts}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Contracts</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{activeContracts}</Typography>
                  <Typography variant="body2" color="text.secondary">Active</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Warning sx={{ color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{expiringSoon}</Typography>
                  <Typography variant="body2" color="text.secondary">Expiring Soon</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AttachMoney sx={{ color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{formatCurrency(totalValue)}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Value</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight="bold">
            Contracts
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchContracts}
          >
            Refresh
          </Button>
        </Box>

        {/* Search Bar */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search contracts by number, name, or account..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Contracts Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Contract #</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={9}>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredContracts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No contracts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContracts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((contract) => {
                      const daysUntilExpiration = getDaysUntilExpiration(contract.endDate);
                      return (
                        <TableRow key={contract._id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {contract.contractNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>{contract.name}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Business fontSize="small" color="action" />
                              {getAccountName(contract.account)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {typeof (contract as any).partner === 'object' && (contract as any).partner
                              ? (contract as any).partner.name
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip label={contract.type || 'Other'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(contract.contractValue, contract.currency)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarToday fontSize="small" color="action" />
                                {formatDate(contract.endDate)}
                              </Box>
                              {daysUntilExpiration !== null && contract.status === 'Active' && (
                                <Typography
                                  variant="caption"
                                  color={daysUntilExpiration <= 30 ? 'error' : daysUntilExpiration <= 60 ? 'warning.main' : 'text.secondary'}
                                >
                                  {daysUntilExpiration > 0 ? `${daysUntilExpiration} days left` : 'Expired'}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={contract.status}
                              size="small"
                              color={getStatusColor(contract.status)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewContract(contract)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredContracts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Contract Detail Dialog */}
        <Dialog open={openDetailDialog} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Contract Details
          </DialogTitle>
          <DialogContent>
            {viewingContract && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {viewingContract.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {viewingContract.contractNumber}
                      </Typography>
                    </Box>
                    <Chip
                      label={viewingContract.status}
                      color={getStatusColor(viewingContract.status)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Account</Typography>
                  <Typography>{getAccountName(viewingContract.account)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Contract Type</Typography>
                  <Typography>{viewingContract.type || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Contract Value</Typography>
                  <Typography fontWeight="bold">
                    {formatCurrency(viewingContract.contractValue, viewingContract.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Owner</Typography>
                  <Typography>{getOwnerName(viewingContract.owner)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Start Date</Typography>
                  <Typography>{formatDate(viewingContract.startDate)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">End Date</Typography>
                  <Typography>{formatDate(viewingContract.endDate)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Renewal Date</Typography>
                  <Typography>{formatDate(viewingContract.renewalDate)}</Typography>
                </Grid>

                {/* Contract Progress */}
                {viewingContract.status === 'Active' && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Contract Progress</Typography>
                    <Box sx={{ mt: 1 }}>
                      {(() => {
                        const start = new Date(viewingContract.startDate).getTime();
                        const end = new Date(viewingContract.endDate).getTime();
                        const now = Date.now();
                        const progress = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
                        const daysLeft = getDaysUntilExpiration(viewingContract.endDate);
                        return (
                          <>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                '& .MuiLinearProgress-bar': {
                                  background: progress > 80 ? '#f44336' : progress > 60 ? '#ff9800' : '#4caf50'
                                }
                              }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {progress.toFixed(0)}% elapsed
                              </Typography>
                              <Typography variant="caption" color={daysLeft && daysLeft <= 30 ? 'error' : 'text.secondary'}>
                                {daysLeft} days remaining
                              </Typography>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Payment Terms</Typography>
                  <Typography>{viewingContract.paymentTerms || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Billing Frequency</Typography>
                  <Typography>{viewingContract.billingFrequency || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Auto Renew</Typography>
                  <Typography>
                    {viewingContract.autoRenew ? (
                      <Chip label="Yes" size="small" color="success" icon={<Autorenew />} />
                    ) : (
                      <Chip label="No" size="small" variant="outlined" />
                    )}
                  </Typography>
                </Grid>

                {viewingContract.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography>{viewingContract.description}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default ContractsPage;
